import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
}

/**
 * Fallback UI shown when an error is caught
 */
function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        padding: '2rem',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          maxWidth: '500px',
          padding: '2rem',
          backgroundColor: '#fef2f2',
          borderRadius: '8px',
          border: '1px solid #fecaca',
        }}
      >
        <h2 style={{ color: '#991b1b', marginBottom: '1rem', fontSize: '1.5rem' }}>
          {t('error.somethingWentWrong')}
        </h2>
        <p style={{ color: '#7f1d1d', marginBottom: '1.5rem' }}>
          {t('error.apologyMessage')}
        </p>
        {import.meta.env.MODE === 'development' && (
          <details
            style={{
              marginBottom: '1.5rem',
              textAlign: 'left',
              backgroundColor: '#fff',
              padding: '1rem',
              borderRadius: '4px',
              fontSize: '0.875rem',
            }}
          >
            <summary style={{ cursor: 'pointer', fontWeight: 'bold', color: '#991b1b' }}>
              {t('error.errorDetails')}
            </summary>
            <pre
              style={{
                marginTop: '0.5rem',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                color: '#7f1d1d',
              }}
            >
              {error.message}
              {'\n\n'}
              {error.stack}
            </pre>
          </details>
        )}
        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
          <button
            onClick={resetError}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500',
            }}
          >
            {t('error.tryAgain')}
          </button>
          <button
            onClick={() => window.location.href = '/'}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: 'white',
              color: '#dc2626',
              border: '1px solid #dc2626',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '500',
            }}
          >
            {t('error.goToHome')}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<ErrorFallbackProps>;
}

/**
 * Error boundary component that catches JavaScript errors in child components
 * and reports them to Sentry
 */
export function ErrorBoundary({ children, fallback }: ErrorBoundaryProps) {
  const FallbackComponent = fallback || ErrorFallback;

  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <FallbackComponent error={error as Error} resetError={resetError} />
      )}
      onError={(error, componentStack) => {
        console.error('Error caught by ErrorBoundary:', error);
        console.error('Component stack:', componentStack);
      }}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

export default ErrorBoundary;
